import { useEffect, useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import MoreContentRequester from 'components/MoreContentRequester';
import { ActivityCardMenu } from 'components/patient/activities/ActivityMenu';
import NoteContent from 'components/patient/blurbs/activities/NoteContent';
import NoteContentHeader from 'components/patient/blurbs/activities/NoteContentHeader';
import NoteIcon from 'components/patient/blurbs/activities/NoteIcon';
import { EscalationFooter } from 'components/shared/EscalationFooter';
import { dateAtTime } from 'lib/date';
import ArchiveLocationEpisode from 'models/activities/ArchiveLocationEpisode';
import RehabStateChange from 'models/activities/RehabStateChange';
import Activity from 'models/Activity';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { AcuteLocationType } from 'models/LocationType';
import Profile from 'models/Profile';
import { ActivityIndexParams, activityQueryKeys, indexActivities } from 'services/api/activity';
import { colors } from 'styles/theme/colors';

import { Content, DateLabel, ModifiedBlurb, NoteHeader, SectionContainer, Separator } from './Layout';
import SkeletonActivityList from './SkeletonActivityList';

function getBorderColor(activity: Activity) {
  const locationType = activity.createdBy?.locationType;

  if (activity.createdByGroupType?.isProvider) {
    return colors.green100;
  }

  switch (locationType) {
    case 'physician_team':
    case AcuteLocationType.HOSPITAL:
    case AcuteLocationType.PHYSICIAN_GROUP:
    case AcuteLocationType.PAYER:
      return colors.lightBlue100;
    default:
      return colors.black10;
  }
}

function showBorder(activity: Activity) {
  return !(activity instanceof RehabStateChange || activity instanceof ArchiveLocationEpisode);
}

const hasContent = (activity: Activity) =>
  !!activity.data.text ||
  !!activity.attachments.length ||
  !!activity.data.questions?.length ||
  !!activity.data.changes?.length;

type ActivityListProps = {
  profile: Profile;
  episode?: Episode;
  locationEpisode?: LocationEpisode;
  reviewId?: string;
  activitiesParameters?: Partial<ActivityIndexParams>;
  onInitialLoad?: () => void;
};

const ActivityList = (props: ActivityListProps) => {
  const { episode, profile, locationEpisode, reviewId, onInitialLoad } = props;

  const activitiesQueryParams = {
    locationEpisodeId: reviewId ? undefined : locationEpisode?.id,
    reviewId: reviewId,
    include: 'attachments.blob,attachments.fileDeletedByClient',
    ...props.activitiesParameters,
    viewOnly: true,
  };

  const activitiesQuery = useInfiniteQuery({
    queryKey: activityQueryKeys.index(activitiesQueryParams),
    queryFn: ({ pageParam, signal }) => {
      return indexActivities(
        {
          ...activitiesQueryParams,
          page: pageParam,
        },
        signal
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: !!locationEpisode?.id || !!reviewId,
  });

  useEffect(() => {
    if (!activitiesQuery.isLoading && onInitialLoad) {
      onInitialLoad();
    }
  }, [activitiesQuery.isLoading, onInitialLoad]);

  const activities = useMemo(() => {
    return activitiesQuery.data?.pages.flatMap((page) => page.data) ?? [];
  }, [activitiesQuery.data]);

  if (!locationEpisode || !episode || activitiesQuery.isLoading) {
    return <SkeletonActivityList />;
  }

  return (
    <div>
      {activities.map((activity, activityIndex) => (
        <div key={activity.id}>
          {activityIndex > 0 && <Separator />}
          <ModifiedBlurb color={getBorderColor(activity)} $border={showBorder(activity)}>
            <NoteHeader>
              <div className='icon'>
                <NoteIcon activity={activity} />
              </div>
              <div>
                <NoteContentHeader activity={activity} />
                <DateLabel>Added {dateAtTime(activity.createdAt!)}</DateLabel>
              </div>
              <ActivityCardMenu activity={activity} profile={profile} />
            </NoteHeader>
            {hasContent(activity) && (
              <Content>
                <NoteContent activity={activity} />

                {activity.escalation && (!episode.archived || activity.escalation.acknowledged) && (
                  <SectionContainer>
                    <EscalationFooter
                      locationEpisode={locationEpisode}
                      profile={profile}
                      escalation={activity.escalation}
                    />
                  </SectionContainer>
                )}
              </Content>
            )}
          </ModifiedBlurb>
        </div>
      ))}

      <MoreContentRequester resource={activitiesQuery} />
    </div>
  );
};

export default ActivityList;
